import { JSXElement, Match, mergeProps, Show, Switch } from 'solid-js';
import { Localized } from '~/i18n';
import Link from '../Link';
import { getSlug, PublicPagePath } from '../AppRouter/utils';
import { usePortalCustomizationContext } from '~/portal-customization/PortalCustomizations.tsx';

export enum LinkExpiredContext {
  STANDALONE_ADMIN_PORTAL = 'STANDALONE_ADMIN_PORTAL',
  EMBEDDED_ADMIN_PORTAL = 'EMBEDDED_ADMIN_PORTAL',
  SIGNUP = 'SIGNUP',
  LOGIN = 'LOGIN',
  GENERIC = 'GENERIC'
}

export enum ApiRequestFailedContext {
  STANDALONE_PORTAL = 'STANDALONE_PORTAL',
  EMBEDDED_PORTAL = 'EMBEDDED_PORTAL'
}

type PortalLinkExpiredProps = {
  context?: LinkExpiredContext | ApiRequestFailedContext;
};

const TRANSLATION_BASE_PATH = `pages.link_expired`;

const getTranslationKey = (translationStr: string): string =>
  `${TRANSLATION_BASE_PATH}.${translationStr}`;

const LocalizedNavigationLink = ({
  path,
  translationKey,
  className
}: {
  path: PublicPagePath;
  translationKey: string;
  className: string;
}) => (
  <Localized
    translationKey={translationKey}
    vars={{
      link: (str: string) => (
        <Link
          href={'#'}
          onClick={() => window.location.assign(getSlug.publicPage(path, true))}
          class={className}
        >
          {str}
        </Link>
      )
    }}
  />
);

export default function PortalLinkExpired(
  props?: PortalLinkExpiredProps
): JSXElement {
  const merged = mergeProps({}, props);
  const portalCustomizationContext = usePortalCustomizationContext();

  const renderHeader = () => (
    <Switch
      fallback={
        <Localized
          translationKey={`pages.link_expired.linked_expired_header`}
        />
      }
    >
      <Match
        when={
          merged.context === LinkExpiredContext.EMBEDDED_ADMIN_PORTAL ||
          merged.context === LinkExpiredContext.STANDALONE_ADMIN_PORTAL
        }
      >
        <Localized
          translationKey={`pages.link_expired.admin_portal_linked_expired_header`}
        />
      </Match>
      <Match when={merged.context && merged.context in ApiRequestFailedContext}>
        <Localized
          translationKey={`pages.link_expired.admin_portal_api_failed_header`}
        />
      </Match>
    </Switch>
  );

  const renderDescription = () => (
    <Switch
      fallback={
        <Localized
          translationKey={getTranslationKey(`linked_expired_description`)}
        />
      }
    >
      <Match when={merged.context === LinkExpiredContext.SIGNUP}>
        <LocalizedNavigationLink
          path={PublicPagePath.SIGNUP}
          translationKey={getTranslationKey(`signup_link_expired_description`)}
          className={'underline hover:cursor-pointer'}
        />
      </Match>
      <Match when={merged.context === LinkExpiredContext.LOGIN}>
        <LocalizedNavigationLink
          path={PublicPagePath.LOGIN}
          translationKey={getTranslationKey(`login_link_expired_description`)}
          className={'underline hover:cursor-pointer'}
        />
      </Match>
      <Match
        when={merged.context === LinkExpiredContext.STANDALONE_ADMIN_PORTAL}
      >
        <Show
          when={portalCustomizationContext?.appConfig?.supportEmail}
          fallback={
            <Localized
              translationKey={getTranslationKey(
                `standalone_portal_link_expired_description`
              )}
            />
          }
        >
          <Localized
            translationKey={getTranslationKey(
              `standalone_portal_support_email_link_expired_description`
            )}
            vars={{
              supportEmail: portalCustomizationContext.appConfig!.supportEmail,
              mailToSupport: () => (
                <a
                  class={`text-link-primary`}
                  href={`mailto:${portalCustomizationContext.appConfig!.supportEmail}`}
                >
                  {portalCustomizationContext.appConfig!.supportEmail}
                </a>
              )
            }}
          />
        </Show>
      </Match>
      <Match
        when={merged.context === ApiRequestFailedContext.STANDALONE_PORTAL}
      >
        <Localized
          translationKey={getTranslationKey(
            `standalone_portal_request_failed_description`
          )}
        />
      </Match>
      <Match
        when={
          merged.context === LinkExpiredContext.EMBEDDED_ADMIN_PORTAL ||
          merged.context === ApiRequestFailedContext.EMBEDDED_PORTAL
        }
      >
        <Localized
          translationKey={getTranslationKey(
            `embedded_portal_link_expired_description`
          )}
        />
      </Match>
    </Switch>
  );

  return (
    <div class={`flex flex-col w-full justify-center items-center`}>
      <div class={`text-2xl`}>{renderHeader()}</div>
      <div class={`mt-9`}>{renderDescription()}</div>
    </div>
  );
}
